import { useEffect, useState } from 'react'

import useAsyncEffect from '@hooks/useAsyncEffect'
import { useDataObject } from '@hooks/useDataObject'
import useDebounce from '@hooks/useDebounce'
import { useStripeContext } from '@providers/StripeProvider'
import { useTitle } from '@providers/TitleProvider'
import CommerceService from '@services/CommerceService'
import { useSession } from '@stores/globalData/globalData.store'
import { ApiRoutes } from '@utils/api-routes'

import type { CustomerCardAddress } from '@models/commerce/CustomerCardAddress'
import type { CustomerCardType } from '@models/commerce/CustomerCardType'

export enum FormState {
  EXISTING_CARD = 'existing',
  NEW_CARD = 'new',
  PENDING = 'pending',
}

export interface FormDataType extends CustomerCardAddress {
  // first_name and last_name are used to update user profile upon purchase
  // not required for payment proccessor per se
  first_name: string
  last_name: string
  saveCard: boolean
}

export const initialFormData = {
  name: '', // name on card for payment verification
  first_name: '', // NF profile name to be updated if missing
  last_name: '',
  country: 'US',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postal_code: '',
  saveCard: false, // not an API parameter, but a front end toggle. hence camel case
}

const usePaymentForm = ({ price, setTaxApiParams, onChangeValidity }) => {
  const session = useSession()
  const { title } = useTitle()

  if (session.currentUser?.profile) {
    initialFormData.first_name = session.currentUser?.profile.first_name || ''
    initialFormData.last_name = session.currentUser?.profile.last_name || ''
  }

  const [formData, setFormData] = useDataObject<FormDataType>(initialFormData)
  const [token, setToken] = useState<string | null>(null)
  const [cards, setCards] = useState<CustomerCardType[]>([])
  const [selectedCard, setSelectedCard] = useState<CustomerCardType>()
  const [uiState, setUiState] = useState<FormState>(FormState.NEW_CARD)
  const [isValid, setIsValid] = useState(false)
  const { stripePromise } = useStripeContext()
  const debouncedFormData = useDebounce(formData, 300)

  const updateValidity = (val: boolean) => {
    if (typeof onChangeValidity === 'function') {
      onChangeValidity(val)
    }
    setIsValid(val)
  }

  const transitionUiState = (newState: FormState) => {
    // saved card and new card forms have different validation criteria
    setIsValid(false)
    setUiState(newState)
  }
  useAsyncEffect(async (isActive) => {
    transitionUiState(FormState.PENDING)
    const cards = await CommerceService.getCustomerCards()
    if (isActive()) {
      if (cards?.length) {
        setCards(cards)
        transitionUiState(FormState.EXISTING_CARD)
      } else {
        transitionUiState(FormState.NEW_CARD)
      }
    }
  }, [])

  useEffect(() => {
    if (!isValid) return
    switch (uiState) {
      case FormState.EXISTING_CARD:
        if (selectedCard) {
          setTaxApiParams({
            route: ApiRoutes.apiTaxLookupCard(),
            params: {
              product_id: title.product?.uuid,
              price: price.getPrice(),
              source_id: selectedCard.id,
            },
          })
        }
        break
      case FormState.NEW_CARD:
        setTaxApiParams({
          route: ApiRoutes.apiTaxLookupAddress(),
          params: {
            product_id: title.product?.uuid,
            price: price.getPrice(),
            ...debouncedFormData,
          },
        })
        break
      default:
    }
  }, [uiState, isValid, selectedCard, debouncedFormData])

  useEffect(() => {
    switch (uiState) {
      case FormState.EXISTING_CARD:
        updateValidity(!!(selectedCard && selectedCard.id) && price.priceInCents > 0)
        break
      case FormState.NEW_CARD:
        updateValidity(!!token && price.priceInCents > 0)
        break
      default:
        updateValidity(false)
    }
  }, [uiState, selectedCard, token, price.priceInCents])

  const onSubmitForm = async () => {
    switch (uiState) {
      case FormState.EXISTING_CARD:
        return selectedCard
      case FormState.NEW_CARD:
        try {
          if (!token) {
            transitionUiState(FormState.NEW_CARD)
            return
          }
          return await CommerceService.createCustomerCard(token, !!formData.saveCard)
        } catch (error) {
          transitionUiState(FormState.NEW_CARD)
          throw error // let the parent component change ui state on error
        }
    }
  }
  return {
    isValid,
    setIsValid,
    selectedCard,
    cards,
    uiState,
    stripePromise,
    formData,
    setFormData,
    token,
    setToken,
    setSelectedCard,
    onSubmitForm,
    transitionUiState,
  }
}

export default usePaymentForm
